import { useRef, type FC } from 'react';
import { useInView } from 'framer-motion';

import {
  HERO_MASCOT_CHOREOGRAPHIES,
  isHeroMascotPreset,
  isSectionMascotPreset,
  SECTION_MASCOT_CHOREOGRAPHIES,
  type HeroMascotPreset,
  type MascotAnchor,
  type SectionMascotPreset,
} from '#/lib/mascot';

import { BlobMascotStep } from './BlobMascotStep';

type Props = {
  /** Hero intro preset or mid-page peer preset. */
  preset: HeroMascotPreset | SectionMascotPreset | string | null | undefined;
  /** Render only the steps with these anchors (card-hosted presets). */
  anchors?: ReadonlyArray<MascotAnchor>;
};

/**
 * Renders a section-local mascot choreography. Parent must be `relative`
 * (heroes already use `overflow-hidden`, which acts as the peek clip).
 *
 * Steps mount only once the host section is meaningfully in view, so the
 * CSS delay/duration clock starts on viewport enter rather than page load.
 * Every choreography is one-shot: it latches on first entry and never
 * replays, even if the section scrolls out and back in.
 */
export const SectionMascot: FC<Props> = ({ preset, anchors }) => {
  const sentinelRef = useRef<HTMLSpanElement>(null);

  const allSteps = isHeroMascotPreset(preset)
    ? HERO_MASCOT_CHOREOGRAPHIES[preset]
    : isSectionMascotPreset(preset)
      ? SECTION_MASCOT_CHOREOGRAPHIES[preset]
      : null;
  const steps = anchors
    ? allSteps?.filter((step) => anchors.includes(step.anchor))
    : allSteps;

  const inView = useInView(sentinelRef, { amount: 0.35, once: true });

  if (!steps?.length) return null;

  return (
    <>
      {/* Sentinel covers the host section so we can observe it without a ref. */}
      <span
        ref={sentinelRef}
        aria-hidden="true"
        className="pointer-events-none absolute inset-0 -z-10"
      />
      {inView
        ? steps.map((step, index) => (
            <BlobMascotStep
              key={`${step.color}-${step.anchor}-${index}`}
              step={step}
            />
          ))
        : null}
    </>
  );
};
